import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Being thrown out, honestly.
 *
 * An admin session is an IDLE window (server/src/app.ts sets `rolling`), and
 * before this file nothing in the client knew when it closed. The shell asked
 * /admin/me once, on mount, and never again; every later 401 surfaced wherever
 * it happened — a red pill on the Form Builder's save indicator — while the
 * screen went on looking signed in. Somebody could spend an afternoon editing
 * a form that was not being saved, and only find out by navigating.
 *
 * Two halves, matching the two ways a session can end under you:
 *
 *   reportSessionExpired  the API answered 401 — you already tried something
 *                         and it failed. Raised by apiFetch, once, for the
 *                         whole burst of calls a screen has in flight.
 *   the idle watchdog     nothing has been asked of the server for the whole
 *                         window, so the session is gone whether or not you
 *                         have touched anything. Nothing is sent to find out:
 *                         a heartbeat would keep alive the very session it was
 *                         checking, and an idle timeout that never fires is
 *                         not an idle timeout.
 *
 * Both end the same way — at /login, saying why, holding on to where you were.
 */

/** What /login shows when it is reached this way, rather than by choice. */
export const EXPIRED_NOTICE = 'Your session timed out. Sign in again to pick up where you left off.';

/** The marker on the API's 401 — see SESSION_EXPIRED in server/src/middleware/adminAuth.ts. */
export const SESSION_EXPIRED = 'session_expired';

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * Whether the burst has already been answered.
 *
 * A screen that loads six things at once gets six 401s. Being told once is
 * being told; the flag clears as soon as a request succeeds again, which is
 * what signing back in does.
 */
let reported = false;

/**
 * Raised by apiFetch when the API says the session is gone.
 *
 * Silent when nobody is listening — that is the marketing site and the login
 * page, where /admin/me answering 401 means "a stranger", not "thrown out".
 * Silent AND forgotten, note: an unheard 401 must not set the flag, or it
 * deafens the next one. A shell can be unarmed for a moment while it is still
 * mounting — `idleMs` arrives with /admin/me, and a request a screen fired
 * alongside it can lose that race — and a request in flight from a tree that
 * has just gone away answers with nobody left to tell. Latching on either
 * would mean the real 401, the one with a shell listening, returns early and
 * says nothing: back to editing for an hour into a session that ended.
 */
export function reportSessionExpired(): void {
  if (reported || !listeners.size) return;
  reported = true;
  for (const fn of listeners) fn();
}

/** A request came back fine, so the session is alive and the window restarts. */
export function noteSessionActivity(): void {
  reported = false;
  lastActivity = Date.now();
}

/** When the server last confirmed this session — the idle window counts from here. */
let lastActivity = Date.now();

/** For tests: the watchdog's deadline, in ms since epoch. */
export const idleDeadline = (idleMs: number): number => lastActivity + idleMs;

/**
 * The signed-in shell's half of the deal: go to the login screen the moment
 * this session stops being one, however that happens.
 *
 * Mounted by AdminLayout and PlatformLayout — the two shells you can only be
 * inside while signed in. `idleMs` is the window, from /admin/me, so the
 * watchdog and the server close it at the same moment; it is also the proof
 * that there WAS a session, which is why nothing is armed until it arrives.
 * A stranger who types /admin is turned away by the shell's own 401, without
 * being told a session of theirs timed out.
 */
export function useSessionExpiry(idleMs: number): void {
  const navigate = useNavigate();
  const location = useLocation();
  /** Where to send them, read through a ref so the listener is registered once. */
  const where = useRef({ pathname: location.pathname, search: location.search });
  where.current = { pathname: location.pathname, search: location.search };

  useEffect(() => {
    if (!idleMs) return;
    let done = false;
    const leave = () => {
      if (done) return;
      done = true;
      const from = `${where.current.pathname}${where.current.search}`;
      navigate('/login', { replace: true, state: { notice: EXPIRED_NOTICE, from } });
    };
    listeners.add(leave);
    return () => { listeners.delete(leave); };
  }, [navigate, idleMs]);

  useEffect(() => {
    if (!idleMs) return;
    /*
     * Re-armed rather than run on an interval: every successful call moves
     * `lastActivity`, so the timer asks "has the window closed?" at what was
     * the deadline when it was set, and re-arms for the new one if not. The
     * grace is for the round trip the server's own clock is ahead by — being
     * a minute late to the login screen costs nothing, being a minute early
     * would send somebody there while their session was still good.
     */
    const GRACE_MS = 60_000;
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      const due = idleDeadline(idleMs) + GRACE_MS - Date.now();
      timer = setTimeout(() => {
        if (Date.now() < idleDeadline(idleMs) + GRACE_MS) { arm(); return; }
        reportSessionExpired();
      }, Math.max(due, 1000));
    };
    arm();
    return () => clearTimeout(timer);
  }, [idleMs]);
}
